import moment from "moment-timezone";
import React from "react";
import { Field, Form } from "react-final-form";
import {
  Form as SemanticForm,
  Header,
  Popup,
  Icon,
  Divider,
  Segment,
  Accordion,
  AccordionTitleProps
} from "semantic-ui-react";
import {
  defaultRegionLegends,
  timeZoneLegends,
  userMeasurementSystemIdLegends
} from "../../../../config/legends/regionSettings";
import { RegionSettingsPerUserSlice } from "../../../../store/regionSettingsPerUser";
import { DateFormat, TimeFormat } from "../../../../types/dateTimeFormats";
import { MeasurementSystem } from "../../../../types/measurementSystems";
import { Region } from "../../../../types/regions";
import { SelectInput, TextInput } from "../../../../utils/components";
import { doesNotExist, exists } from "../../../../utils/utils";
import DateAndTimeFormatPicker from "../../../common/DateAndTimeFormatPicker";

type UserProps = {
  /** Function to call when submitting form. */
  onSubmit: (...args: any[]) => void;
  /** Region current settings per user. */
  regionSettings: RegionSettingsPerUserSlice;
  /** Available measurement systems. */
  measurementSystems: MeasurementSystem[];
  /** Should form be disabled? */
  disabled: boolean;
  /** Available regions. */
  availableRegions: Region[];
  /** Default region ID. */
  defaultRegionId: number | null;
  /** Available date formats. */
  dateFormats: DateFormat[];
  /** Available time formats. */
  timeFormats: TimeFormat[];
  /** Default time zone. */
  defaultTimeZone?: string;
  /** Are date time formats loading? */
  areDateTimeFormatsLoading: boolean;
  /** Are measurement systems loading? */
  areMeasurementSystemsLoading: boolean;
};

type UserState = { activeIndex: number };

class User extends React.Component<UserProps, UserState> {
  constructor(props: UserProps) {
    super(props);
    this.state = {
      activeIndex: -1
    };
  }

  static defaultProps = {
    areDateTimeFormatsLoading: false,
    areMeasurementSystemsLoading: false,
    disabled: false
  };

  disabledFields = ["defaultRegion", "timeZone"];

  handleClick = (e: any, titleProps: AccordionTitleProps) => {
    const { index } = titleProps;
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({ activeIndex: newIndex as number });
  };

  getTimeZoneOptions = (options = moment.tz.names()) => {
    const mappedOptions = options.map(timeZone => ({
      key: timeZone,
      value: timeZone,
      text: timeZone
    }));
    return [
      ...[
        {
          key: "dynamic",
          value: "dynamic",
          text: `Dynamic (current location: ${moment.tz.guess()})`
        }
      ],
      ...mappedOptions
    ];
  };

  getMeasurementSystemOptions = (options = this.props.measurementSystems) =>
    exists(options)
      ? options.map(({ id, title }) => ({
          key: id,
          value: id,
          text: title
        }))
      : null;

  getAvailableRegionOptions = (options = this.props.availableRegions) =>
    exists(options)
      ? options.map(({ id, name }) => ({
          key: id,
          value: id,
          text: name
        }))
      : null;

  hasInitialValuesChanged = (
    values: Record<string, any> | undefined,
    previousValues: Record<string, any> | undefined
  ) => JSON.stringify(values) === JSON.stringify(previousValues);

  /**
   * Render field with info icon
   *
   * @param {String} key                        - Field key
   * @param {String} name                       - Field name
   * @param {String} label                      - Field label
   * @param {String} infoContent                - Field icon tooltip content
   * @param {Array<Object> | null} options      - Options for select field
   * @param {Boolean | null} isLoading          - Should render field as loading?
   * @return {JSX.Element}                      - Returns SemanticForm Field
   */
  renderFieldWithInfoIcon = (
    key: string,
    name: string,
    label: string,
    infoContent: string,
    options: any[] | null = null,
    isLoading: boolean | null = false
  ): JSX.Element => (
    <SemanticForm.Field>
      <label style={{ display: "inline" }}>{label}</label>
      <Popup trigger={<Icon name="info circle" color="blue" style={{ float: "right" }} />}>
        {infoContent}
      </Popup>
      <Field
        key={key}
        name={name}
        aria-label={`${label} input`}
        // @ts-ignore
        component={options === null ? TextInput : SelectInput}
        options={options}
        disabled={this.disabledFields.includes(name)}
        placeholder="Region default"
        clearable={options === null ? undefined : true}
        allowNull={true}
        loading={isLoading}
      />
    </SemanticForm.Field>
  );

  validateFormValues = (values: Record<string, any>) => {
    const errors: Record<string, any> = {};
    const requiredFieldText = "This field is required";
    const requiredFields = ["timeZone"];

    requiredFields.forEach(name =>
      doesNotExist(values[name]) ? (errors[name] = requiredFieldText) : null
    );
    return errors;
  };

  render() {
    const { activeIndex } = this.state;

    const { disabled, onSubmit, regionSettings, defaultRegionId, defaultTimeZone } = this.props;
    let initialValues: Record<string, any> = {
      defaultRegion: exists(defaultRegionId) ? defaultRegionId : "",
      timeZone: exists(defaultTimeZone) ? defaultTimeZone : "dynamic",
      measurementSystemId: undefined,
      shortDateFormatId: undefined,
      longDateFormatId: undefined,
      shortTimeFormatId: undefined,
      longTimeFormatId: undefined
    };
    if (exists(regionSettings)) {
      const {
        measurementSystemId,
        shortDateFormatId,
        longDateFormatId,
        shortTimeFormatId,
        longTimeFormatId
      } = regionSettings;
      initialValues = {
        ...initialValues,
        ...{
          measurementSystemId: measurementSystemId ? measurementSystemId : undefined,
          shortDateFormatId: shortDateFormatId ? shortDateFormatId : undefined,
          longDateFormatId: longDateFormatId ? longDateFormatId : undefined,
          shortTimeFormatId: shortTimeFormatId ? shortTimeFormatId : undefined,
          longTimeFormatId: longTimeFormatId ? longTimeFormatId : undefined
        }
      };
    }
    return (
      <>
        <Header as="h2">Your settings</Header>
        <Header as="h3">Customize your UrbanCanvas user interface</Header>
        <Form
          onSubmit={onSubmit}
          initialValues={initialValues}
          initialValuesEqual={this.hasInitialValuesChanged}
          render={({ handleSubmit, invalid, pristine, submitting }) => (
            <Segment>
              <SemanticForm onSubmit={handleSubmit}>
                <Accordion>
                  <Accordion.Title active={activeIndex === 0} index={0} onClick={this.handleClick}>
                    <Header fluid="true" as="h4" style={{ margin: 0 }}>
                      <Icon name="dropdown" />
                      UrbanCanvas settings
                    </Header>
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex === 0}>
                    {this.renderFieldWithInfoIcon(
                      "default_region",
                      "defaultRegion",
                      defaultRegionLegends.label,
                      defaultRegionLegends.infoContent,
                      this.getAvailableRegionOptions()
                    )}
                  </Accordion.Content>
                  <Divider />
                  <Accordion.Title active={activeIndex === 1} index={1} onClick={this.handleClick}>
                    <Header fluid="true" as="h4" style={{ margin: 0 }}>
                      <Icon name="dropdown" />
                      User interface
                    </Header>
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex === 1}>
                    {this.renderFieldWithInfoIcon(
                      "measurement_system_id",
                      "measurementSystemId",
                      userMeasurementSystemIdLegends.label,
                      userMeasurementSystemIdLegends.infoContent,
                      this.getMeasurementSystemOptions(),
                      this.props.areMeasurementSystemsLoading
                    )}
                    {this.renderFieldWithInfoIcon(
                      "time_zone",
                      "timeZone",
                      timeZoneLegends.label,
                      timeZoneLegends.infoContent,
                      this.getTimeZoneOptions()
                    )}
                    <DateAndTimeFormatPicker
                      allowNull={true}
                      dateFormats={this.props.dateFormats}
                      isLoading={this.props.areDateTimeFormatsLoading}
                      timeFormats={this.props.timeFormats}
                    />
                  </Accordion.Content>
                  <Divider />
                </Accordion>
                <SemanticForm.Button
                  disabled={invalid || disabled || pristine}
                  loading={submitting}
                  type="submit"
                >
                  Save
                </SemanticForm.Button>
              </SemanticForm>
            </Segment>
          )}
          validate={values => this.validateFormValues(values)}
        />
      </>
    );
  }
}

export default User;
